import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SearchTypeToggle from './components/SearchTypeToggle';
import SearchForm from './components/SearchForm';
import TicketResults from './components/TicketResults';
import QuickActions from './components/QuickActions';
import { searchPurchases } from './utils/supabaseSearch'; // ← Importación real de Supabase

const TicketVerification = () => {
  const [searchType, setSearchType] = useState('cedula');
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [recentSearches, setRecentSearches] = useState([]);

  // Mock ticket data for fallback (solo si Supabase falla)
  const mockTickets = [
    {
      id: 1,
      ticketNumber: 'AF-2024-001234',
      ownerName: 'María González',
      ownerCedula: 'V-12345678',
      purchaseDate: '2024-11-01T10:30:00Z',
      amount: 50.00,
      status: 'active',
      paymentMethod: 'Pago Móvil',
      bankReference: '123456'
    },
    {
      id: 2,
      ticketNumber: 'AF-2024-001235',
      ownerName: 'María González',
      ownerCedula: 'V-12345678',
      purchaseDate: '2024-11-01T10:30:00Z',
      amount: 50.00,
      status: 'active',
      paymentMethod: 'Pago Móvil',
      bankReference: '123456'
    },
    {
      id: 3,
      ticketNumber: 'AF-2024-000987',
      ownerName: 'Carlos Rodríguez',
      ownerCedula: 'V-87654321',
      purchaseDate: '2024-10-28T15:45:00Z',
      amount: 100.00,
      status: 'winner',
      paymentMethod: 'Zelle',
      bankReference: '789012',
      prizeInfo: {
        description: 'Bs. 500.000 - Segundo Premio',
        drawDate: '2024-11-03T20:00:00Z',
        claimDeadline: '2024-12-03T23:59:59Z'
      }
    },
    {
      id: 4,
      ticketNumber: 'AF-2024-000456',
      ownerName: 'Ana Martínez',
      ownerCedula: 'E-11223344',
      purchaseDate: '2024-10-25T09:15:00Z',
      amount: 75.00,
      status: 'used',
      paymentMethod: 'Binance Pay',
      bankReference: 'BIN456789'
    }
  ];

  useEffect(() => {
    // Load recent searches from localStorage
    const saved = localStorage.getItem('aurea-recent-searches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (error) {
        console.error('Error loading recent searches:', error);
      }
    }
  }, []);

  const saveRecentSearch = (type, value) => {
    const newSearch = {
      id: Date.now(),
      type,
      value,
      timestamp: new Date()?.toISOString()
    };

    const updated = [newSearch, ...recentSearches?.slice(0, 4)];
    setRecentSearches(updated);
    localStorage.setItem('aurea-recent-searches', JSON.stringify(updated));
  };

  // === FUNCIÓN DE BÚSQUEDA MEJORADA ===
  const handleSearch = async () => {
    const cleanValue = searchValue.replace(/\D/g, '');
    if (!cleanValue) {
      setSearchError('Ingresa un valor válido');
      return;
    }

    setIsLoading(true);
    setSearchError('');

    try {
      console.log('🚀 Iniciando búsqueda MEJORADA:', { 
        searchType, 
        original: searchValue, 
        clean: cleanValue 
      });

      // Llamar la función mejorada que retorna { all, confirmed, pending }
      const rawResults = await searchPurchases(searchType, cleanValue);

      console.log('📊 Respuesta de Supabase:', rawResults);

      // Verificar si hay resultados
      if (!rawResults || !rawResults.all || rawResults.all.length === 0) {
        console.log('❌ No se encontraron compras');
        setSearchResults(null);
        return;
      }

      // Procesar TODAS las compras del cliente
      const formatted = rawResults.all.map((purchase, index) => {
        // Formatear números de ticket
        const firstNumber = purchase.numeros?.[0] || 0;
        const ticketNumber = `AF-2024-${String(firstNumber).padStart(6, '0')}`;

        return {
          id: purchase.id,
          ticketNumber,
          ownerName: purchase.nombre_completo,
          ownerCedula: purchase.cedula,
          purchaseDate: purchase.created_at,
          amount: parseFloat(purchase.total_pagado) || 0,
          status: purchase.estado === 'pagado' ? 'active' : 'used',
          paymentMethod: purchase.metodo_pago,
          bankReference: purchase.uuid?.slice(0, 8) || 'N/A',
          // === CAMPOS ADICIONALES PARA EL NUEVO SISTEMA ===
          estado: purchase.estado,           // Estado real de la compra
          numeros: purchase.numeros,         // Array de números de tickets
          uuid: purchase.uuid,              // UUID de la compra
          index: index + 1,                 // Número de compra (1, 2, 3...)
          created_at: purchase.created_at,  // Fecha de creación
          total_pagado: purchase.total_pagado // Total pagado
        };
      });

      console.log('📋 Compras formateadas para mostrar:', formatted.length);
      console.log('📝 Detalles:', formatted);

      // Guardar en el estado (ahora como objeto completo, no solo array simple)
      setSearchResults({
        purchases: formatted,
        summary: {
          total: rawResults.all.length,
          confirmed: rawResults.confirmed.length,
          pending: rawResults.pending.length
        }
      });

      saveRecentSearch(searchType, cleanValue);

    } catch (error) {
      console.error('❌ Error crítico en búsqueda:', error);
      setSearchError('Error de conexión. Revisa la consola.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearResults = () => {
    setSearchResults(null);
    setSearchValue('');
    setSearchError('');
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'recent':
        if (recentSearches?.length > 0) {
          const lastSearch = recentSearches?.[0];
          setSearchType(lastSearch?.type);
          setSearchValue(lastSearch?.value);
        } else {
          alert('No hay búsquedas recientes');
        }
        break;
      case 'bulk': 
        alert('Función de verificación masiva próximamente disponible');
        break;
      case 'download':
        alert('Descarga masiva próximamente disponible');
        break;
      case 'support':
        window.open('https://wa.me/584121234567?text=Hola, necesito ayuda con la verificación de boletos', '_blank');
        break;
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Verificar Boletos - Áurea Fortuna | Lotería Premium</title>
        <meta name="description" content="Verifica tus boletos de lotería en Áurea Fortuna. Busca por cédula o número de boleto para conocer el estado de tus participaciones." />
        <meta name="keywords" content="verificar boletos, lotería venezuela, áurea fortuna, consultar tickets, resultados lotería" />
      </Helmet>
      
      {/* Estilos CSS para animaciones */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }
      `}</style>
      <Header />
      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-navy via-background to-navy py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-secondary/10 rounded-full">
                <Icon name="Search" size={48} className="text-secondary" />
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4">
              Verificación de Boletos
            </h1>
            
            <p className="text-xl text-text-secondary font-caption max-w-2xl mx-auto mb-8">
              Consulta el estado de tus boletos de manera rápida y segura. 
              Ahora muestra TODAS tus compras organizadas por estado.
            </p>

            {/* Elemento decorativo mejorado */}
            <div className="relative max-w-2xl mx-auto animate-fade-in-up" style={{animationDelay: '0.6s'}}>
              <div className="h-px bg-gradient-to-r from-transparent via-secondary/50 to-transparent"></div>
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="w-4 h-4 bg-gradient-to-br from-secondary/60 to-secondary/30 rounded-full animate-pulse"></div>
              </div>
              {/* Efectos decorativos adicionales */}
              <div className="absolute top-0 left-1/4 w-2 h-2 bg-secondary/20 rounded-full animate-bounce" style={{animationDelay: '0s'}}></div>
              <div className="absolute top-0 right-1/4 w-1.5 h-1.5 bg-secondary/25 rounded-full animate-bounce" style={{animationDelay: '1s'}}></div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Search Type Toggle */}
            <SearchTypeToggle
              searchType={searchType}
              onSearchTypeChange={setSearchType}
            />

            {/* Search Form */}
            <SearchForm
              searchType={searchType}
              searchValue={searchValue}
              onSearchValueChange={setSearchValue}
              onSearch={handleSearch}
              isLoading={isLoading}
              error={searchError}
            />

            {/* Recent Searches */}
            {recentSearches?.length > 0 && !searchResults && (
              <div className="bg-card rounded-xl border border-border p-6 shadow-luxury">
                <h3 className="font-body font-semibold text-card-foreground mb-4 flex items-center space-x-2">
                  <Icon name="History" size={18} />
                  <span>Búsquedas Recientes</span>
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {recentSearches?.slice(0, 4)?.map((search) => (
                    <div
                      key={search?.id}
                      onClick={() => {
                        setSearchType(search?.type);
                        setSearchValue(search?.value);
                      }}
                      className="p-3 rounded-lg border border-border hover:border-secondary/50 hover:bg-muted/30 cursor-pointer transition-premium"
                    >
                      <div className="flex items-center space-x-3">
                        <Icon 
                          name={search?.type === 'cedula' ? 'User' : 'Ticket'} 
                          size={16} 
                          className="text-secondary" 
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-sm text-card-foreground truncate">
                            {search?.value}
                          </div>
                          <div className="text-xs text-muted-foreground font-caption">
                            {search?.type === 'cedula' ? 'Por cédula' : 'Por boleto'} • {
                              new Date(search.timestamp)?.toLocaleDateString('es-VE')
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Search Results */}
            {(searchResults !== null || isLoading) && (
              <TicketResults
                // NUEVA INTERFAZ: Pasar los datos estructurados
                searchType={searchType}
                searchValue={searchValue}
                searchResults={searchResults}
                isLoading={isLoading}
                onClearResults={handleClearResults}
              />
            )}

            {/* Quick Actions */}
            {!searchResults && !isLoading && (
              <QuickActions onActionClick={handleQuickAction} />
            )}
          </div>
        </section>

        {/* Help Section */}
        <section className="bg-muted/30 py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
                ¿Cómo verificar tus boletos?
              </h2>
              <p className="text-text-secondary font-caption max-w-2xl mx-auto">
                Sigue estos sencillos pasos para consultar el estado de tus boletos. 
                Ahora puedes ver TODAS tus compras en una sola consulta.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-secondary">1</span>
                </div>
                <h3 className="font-body font-semibold text-foreground mb-2">
                  Selecciona el método
                </h3>
                <p className="text-sm text-text-secondary font-caption">
                  Elige si quieres buscar por tu cédula o por el número específico del boleto
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-secondary">2</span>
                </div>
                <h3 className="font-body font-semibold text-foreground mb-2">
                  Ingresa la información
                </h3>
                <p className="text-sm text-text-secondary font-caption">
                  Escribe tu cédula (30219848) o número de boleto (1234)
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-secondary">3</span>
                </div>
                <h3 className="font-body font-semibold text-foreground mb-2">
                  Consulta TODAS las compras
                </h3>
                <p className="text-sm text-text-secondary font-caption">
                  Ahora ves todas tus compras organizadas por estado: confirmadas y pendientes
                </p>
              </div>
            </div>

            <div className="text-center mt-12">
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="secondary"
                  onClick={() => window.location.href = '/purchase-flow'}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Comprar Boletos
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => window.open('https://wa.me/584121234567', '_blank')}
                  iconName="MessageCircle"
                  iconPosition="left"
                >
                  Contactar Soporte
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Navigation Links */}
        <section className="py-8 px-4 border-t border-border">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/home-landing"
                className="flex items-center space-x-2 px-4 py-2 text-text-secondary hover:text-foreground transition-premium"
              >
                <Icon name="Home" size={16} />
                <span className="font-caption">Inicio</span>
              </Link>
              
              <Link
                to="/purchase-flow"
                className="flex items-center space-x-2 px-4 py-2 text-text-secondary hover:text-foreground transition-premium"
              >
                <Icon name="ShoppingCart" size={16} />
                <span className="font-caption">Comprar Boletos</span>
              </Link>
              
              {/* ⚠️ OCULTO TEMPORALMENTE - Estado de Pagos (2025-11-17) */}
              {/*
              <Link
                to="/payment-confirmation"
                className="flex items-center space-x-2 px-4 py-2 text-text-secondary hover:text-foreground transition-premium"
              >
                <Icon name="CreditCard" size={16} />
                <span className="font-caption">Estado de Pagos</span>
              </Link>
              */}
            </div>
          </div>
        </section>
      </main>
      {/* WhatsApp Float Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          variant="secondary"
          size="icon"
          onClick={() => window.open('https://wa.me/584121234567?text=Hola, necesito ayuda con la verificación de boletos', '_blank')}
          className="w-14 h-14 rounded-full shadow-luxury hover:shadow-xl animate-bounce"
        >
          <Icon name="MessageCircle" size={24} />
        </Button>
      </div>
    </div>
  );
};

export default TicketVerification;